import type { VertexStyle } from '../../mesh/meshBuilder';
import { SURFACE, type SurfaceKind } from '../../mesh/attributes';
import type { MasonryStyles } from './masonry';
import type { DamColors } from './dam';
import { hexToLinear, scaleColor, type LinearRGB } from '../../../core/color';
import type { PaletteModel } from '../../../data/types';
import type { Rng } from '../../../core/rng';
import { sampleSwatch } from '../../../data/palette';
import { infillColors } from '../houseStyle';
import { darkestCentre, nearestCentre } from './paletteCentres';
import { dressingStone } from './tracery/traceryTemplates';
import { LANDMARK_PATTERN, patterned } from './surfacePatterns';

/** One palette sample per material, drawn once per landmark. */
export interface Materials {
  readonly stone: LinearRGB;
  readonly slate: LinearRGB;
  /** Church and chapel roofs. */
  readonly churchSlate: LinearRGB;
  /** The town hall's brick, painted near black-brown (Lab 21.7 9.4 26.4, stadhuis-scan.jpg). */
  readonly hallBrick: LinearRGB;
  /**
   * The Oude Kerk's walls, painted dark olive (#4b461e on the north chapels, #504b20 on the choir,
   * oudekerk-scan.jpg): the darkest tower stone, which the shade ramp lifts least.
   */
  readonly kerkWall: LinearRGB;
  /** Lead, painted as light as the middle slate: the Oude Kerk's lantern, #696235 (scan 3058-3075 x 2583-2603). */
  readonly lead: LinearRGB;
  /**
   * The Nieuwe Kerk's brick, painted #645122 on the choir's clerestory and #5d5125 on the nave's
   * (nieuwekerk-scan, scan 3700-3760 x 1885-1925 and 3860-3880 x 1845-1860): the nearest brick centre.
   */
  readonly nieuweKerkWall: LinearRGB;
  /** The churches' window stone (tracery/traceryTemplates.ts). */
  readonly dressing: LinearRGB;
  readonly tile: LinearRGB;
  readonly plaster: LinearRGB;
  readonly brick: LinearRGB;
  readonly timber: LinearRGB;
  readonly dark: LinearRGB;
  readonly earth: LinearRGB;
  readonly glass: LinearRGB;
  readonly leaf: LinearRGB;
  readonly treeFoliage: LinearRGB;
  /**
   * The painted rope-walk roofs are pale, nearest wallLight among the building classes (dE 3.1-6.1),
   * and in the painter view as dark as its darkest centre: the painting under them reads L* 36 at the
   * median, where the class's centres drew them at 48 (landmarks.md, "Rope-walk roofs").
   */
  readonly reed: LinearRGB;
  /** The painted garden ground, which a cloister garth is. */
  readonly garth: LinearRGB;
  /** The painted tenter rows are near black (L* 9 in their darkest third) with a few red bays. */
  readonly cloth: LinearRGB;
  readonly dyed: LinearRGB;
}

const PAINTED_LEAD = hexToLinear('#696235');
const PAINTED_NIEUWE_KERK_WALL = hexToLinear('#645122');

export function materialsOf(palette: PaletteModel, rng: Rng): Materials {
  return {
    stone: sampleSwatch(palette.towerStone, rng.fork('stone')),
    slate: sampleSwatch(palette.roofSlate, rng.fork('slate')),
    churchSlate: darkestCentre(palette.roofSlate),
    hallBrick: darkestCentre(palette.wallBrick),
    kerkWall: darkestCentre(palette.towerStone),
    lead: nearestCentre(palette.roofSlate, PAINTED_LEAD),
    nieuweKerkWall: nearestCentre(palette.wallBrick, PAINTED_NIEUWE_KERK_WALL),
    dressing: dressingStone(palette),
    tile: sampleSwatch(palette.roofTile, rng.fork('tile')),
    plaster: sampleSwatch(palette.wallLight, rng.fork('plaster')),
    brick: sampleSwatch(palette.wallBrick, rng.fork('brick')),
    timber: sampleSwatch(palette.shipHull, rng.fork('timber')),
    dark: palette.lineworkDark.median.linear,
    earth: palette.streetGround.median.linear,
    ...infillColors(palette),
    treeFoliage: palette.treeFoliage.median.linear,
    reed: darkestCentre(palette.wallLight),
    garth: palette.gardenGrass.median.linear,
    cloth: palette.shipHull.p10.linear,
    dyed: palette.flagRed.p10.linear,
  };
}

export function masonryStyles(
  wall: { color: LinearRGB; surface: SurfaceKind },
  roof: { color: LinearRGB; surface: SurfaceKind },
  m: Materials,
  ids: { edgeId: number; seed: number }
): MasonryStyles {
  const base = { ...ids, strokeAngle: 0, extent: [10, 10] as const };
  const style = (s: { color: LinearRGB; surface: SurfaceKind }): VertexStyle => ({ ...base, ...s });
  return {
    wall: style(wall),
    roof: style(roof),
    opening: style({ color: m.dark, surface: SURFACE.linework }),
  };
}

/** The painted western water tower has a dark slate cap. Module sizes use the period preset. */
export function nieuweBrugMasonry(
  id: string,
  m: Materials,
  ids: { edgeId: number; seed: number }
): MasonryStyles {
  const west = id === 'landmark-nieuwe-brug-west-tower';
  const masonry = masonryStyles(
    { color: m.brick, surface: SURFACE.brick },
    { color: west ? m.churchSlate : m.slate, surface: SURFACE.roofSlate },
    m,
    ids
  );
  return west ? { ...masonry, roof: patterned(masonry.roof, LANDMARK_PATTERN.slate) } : masonry;
}

/* The painting's sluice gates are near black, darker than the hulls the palette samples. */
const DAM_GATE_DARKEN = 0.45;

/** The Dam's stone, paving, houses and gate; its railing's coping and finials in the lighter plaster. */
export function damColors(m: Materials): DamColors {
  return {
    wall: m.stone,
    pavement: m.earth,
    gate: scaleColor(m.timber, DAM_GATE_DARKEN),
    house: m.brick,
    slate: m.slate,
    roof: m.tile,
    opening: m.glass,
    trim: m.plaster,
  };
}
